import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Command as CommandPrimitive } from 'cmdk';
import { Check } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { isStructuredOption } from '../../lib/autocomplete-option';
import { usePortalContainer } from '../../lib/portal-container';
import { cn } from '../../lib/utils';
import { AutocompleteOptionRow } from './autocomplete-option-row';
import { CommandGroup, CommandInput, CommandItem, CommandList } from './command';
import { Skeleton } from './skeleton';
/** Largeur minimale de la liste déroulante, pour que les libellés restent lisibles. */
const MIN_DROPDOWN_WIDTH = 360;
/** Marge conservée avec les bords du viewport quand la liste est élargie. */
const VIEWPORT_MARGIN = 8;
export function AutoComplete({ className, isInvalid = false, autoFocus, disabled, emptyMessage, icon, isLoading = false, onBlurCallBack, onInputChange, onOpenChange, onValueChange, options, placeholder, shouldFilter, value, }) {
    const inputRef = useRef(null);
    const portalContainer = usePortalContainer();
    const [isOpen, setIsOpen] = useState(false);
    const setOpen = useCallback((open) => {
        setIsOpen(open);
        onOpenChange?.(open);
    }, [onOpenChange]);
    const [selected, setSelected] = useState(value);
    const [inputValue, setInputValue] = useState(value?.label ?? '');
    // Reflète un changement de la prop `value` (contrôlée par le parent) dans l'état
    // interne, éditable par l'utilisateur. Ajustement pendant le rendu plutôt qu'un
    // `setState` synchrone dans un effet — cf. la doc React « Adjusting state when a
    // prop changes ».
    const [previousValue, setPreviousValue] = useState(value);
    if (previousValue !== value) {
        setPreviousValue(value);
        setSelected(value);
        setInputValue(value?.label ?? '');
    }
    // Notify parent of input changes
    const handleInputChange = useCallback((newValue) => {
        setInputValue(newValue);
        onInputChange?.(newValue);
    }, [onInputChange]);
    const handleKeyDown = useCallback((event) => {
        const input = inputRef.current;
        if (!input) {
            return;
        }
        // Keep the options displayed when the user is typing
        if (!isOpen) {
            setOpen(true);
        }
        // This is not a default behaviour of the <input /> field
        if (event.key === 'Enter' && input.value !== '') {
            const optionToSelect = options.find((option) => option.label === input.value);
            if (optionToSelect) {
                setSelected(optionToSelect);
                onValueChange?.(optionToSelect);
            }
        }
        if (event.key === 'Escape') {
            input.blur();
        }
        if (event.key === 'Backspace' && selected) {
            setSelected(undefined);
            setInputValue('');
            // @ts-expect-error clear selection
            onValueChange?.();
            return;
        }
    }, [isOpen, options, onValueChange, selected, setOpen]);
    const handleBlur = useCallback(() => {
        setOpen(false);
        setInputValue(selected?.label ?? '');
    }, [selected, setOpen]);
    const handleSelectOption = useCallback((selectedOption) => {
        setInputValue(selectedOption.label);
        setSelected(selectedOption);
        onValueChange?.(selectedOption);
        setOpen(false);
    }, [onValueChange, setOpen]);
    // compute rect for portal dropdown
    const [dropdownStyle, setDropdownStyle] = useState();
    useEffect(() => {
        if (!isOpen || !inputRef.current) {
            return;
        }
        const updateDropdownPosition = () => {
            if (!inputRef.current) {
                return;
            }
            const rect = inputRef.current.getBoundingClientRect();
            // Un champ étroit (widget WordPress, colonne serrée) donnerait une liste
            // illisible : on lui garantit un plancher, tout en la ramenant dans le
            // viewport si l'élargissement la ferait déborder à droite.
            const width = Math.min(Math.max(rect.width, MIN_DROPDOWN_WIDTH), Math.max(document.documentElement.clientWidth - VIEWPORT_MARGIN * 2, MIN_DROPDOWN_WIDTH));
            const maxLeft = document.documentElement.clientWidth - width - VIEWPORT_MARGIN;
            const clampedLeft = Math.max(VIEWPORT_MARGIN, Math.min(rect.left, maxLeft));
            if (portalContainer !== null && portalContainer !== document.body) {
                const containerRect = portalContainer.getBoundingClientRect();
                setDropdownStyle({
                    left: clampedLeft - containerRect.left,
                    position: 'absolute',
                    top: rect.bottom - containerRect.top,
                    width,
                    zIndex: 60,
                });
                return;
            }
            setDropdownStyle({
                left: clampedLeft + window.scrollX,
                position: 'absolute',
                top: rect.bottom + window.scrollY,
                width,
                zIndex: 60,
            });
        };
        updateDropdownPosition();
        window.addEventListener('resize', updateDropdownPosition);
        window.addEventListener('scroll', updateDropdownPosition, true);
        return () => {
            window.removeEventListener('resize', updateDropdownPosition);
            window.removeEventListener('scroll', updateDropdownPosition, true);
        };
    }, [isOpen, portalContainer]);
    const dropdown = (_jsx("div", { className: cn('animate-in fade-in-0 zoom-in-95 border-input pointer-events-auto rounded-lg border bg-white shadow-md outline-none', isOpen && !disabled ? 'block' : 'hidden'), style: dropdownStyle, children: _jsxs(CommandList, { className: "max-h-[26rem] rounded-lg", children: [isLoading ? (_jsx(CommandPrimitive.Loading, { children: _jsx("div", { className: "p-1", children: _jsx(Skeleton, { className: "h-8 w-full" }) }) })) : null, options.length > 0 && !isLoading ? (_jsx(CommandGroup, { children: options.map((option) => {
                        const isSelected = selected?.value === option.value;
                        const optionKey = option.key ?? option.value;
                        return (_jsx(CommandItem, { className: cn('flex w-full cursor-pointer items-center gap-2 px-2 py-1.5 hover:bg-gray-100', 
                            // cmdk sélectionne déjà l'item au survol : le survol gris d'origine
                            // est neutralisé au profit d'un état de sélection aux couleurs brand.
                            isStructuredOption(option) &&
                                'data-[selected=true]:bg-brand-50/60 hover:bg-brand-50/60', option.isChild && 'airport-child', option.isChild && option.isLastChild && 'airport-child-last'), onMouseDown: (event) => {
                                event.preventDefault();
                                event.stopPropagation();
                            }, onSelect: () => {
                                handleSelectOption(option);
                            }, 
                            // Identité cmdk : le libellé n'est pas forcément unique (deux
                            // villes « Paris », en France et aux États-Unis). Les options
                            // sans `key` gardent le libellé — tous les consommateurs sont
                            // en shouldFilter={false}, cette valeur ne sert qu'au surlignage.
                            value: option.key ?? option.label, children: _jsx(AutocompleteOptionRow, { iconFallback: isSelected ? _jsx(Check, { className: "h-4 w-4" }) : null, option: option }) }, optionKey));
                    }) })) : null, !isLoading ? (_jsx(CommandPrimitive.Empty, { className: "rounded-sm px-2 py-3 text-center text-sm text-gray-500 select-none", children: emptyMessage })) : null] }) }));
    return (_jsxs(CommandPrimitive, { className: "w-full", onKeyDown: handleKeyDown, shouldFilter: shouldFilter, children: [_jsx("div", { className: "w-full cursor-text", onClick: () => inputRef.current?.focus(), children: _jsx(CommandInput, { "aria-invalid": isInvalid || undefined, autoFocus: autoFocus, className: cn('border-input bg-background text-foreground focus-visible:border-ring focus-visible:ring-ring/20 flex h-9 w-full cursor-text rounded-lg border py-2 text-sm shadow-sm shadow-black/5 transition-shadow placeholder:text-gray-500 focus-visible:ring-[3px] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50', className), disabled: disabled, icon: icon, onBlur: () => {
                        handleBlur();
                        onBlurCallBack?.();
                    }, onFocus: (e) => {
                        setOpen(true);
                        e.target.select();
                    }, onValueChange: (s) => {
                        handleInputChange(s);
                    }, placeholder: placeholder, ref: inputRef, value: inputValue }) }), isOpen && createPortal(dropdown, portalContainer ?? document.body)] }));
}
